export interface LoadScriptOptions {
  async?: boolean;
  defer?: boolean;
  timeout?: number;
  attrs?: Record<string, string>;
}

export interface LoadScriptResult {
  success: boolean;
  error?: Error;
}

export function loadScript(
  url: string,
  options: LoadScriptOptions = {}
): Promise<LoadScriptResult> {
  const { async = true, defer = false, timeout = 5000, attrs = {} } = options;

  return new Promise((resolve) => {
    const existingScript = document.querySelector(`script[src="${url}"]`);
    if (existingScript) {
      resolve({ success: true });
      return;
    }

    const script = document.createElement('script');
    script.src = url;
    script.async = async;
    script.defer = defer;

    Object.entries(attrs).forEach(([key, value]) => {
      script.setAttribute(key, value);
    });

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let resolved = false;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }
    };

    const handleSuccess = () => {
      if (resolved) return;
      resolved = true;
      cleanup();
      resolve({ success: true });
    };

    const handleError = (error?: Error | Event) => {
      if (resolved) return;
      resolved = true;
      cleanup();
      const err = error instanceof Error
        ? error
        : new Error(`Failed to load script: ${url}`);
      resolve({ success: false, error: err });
    };

    script.onload = handleSuccess;
    script.onerror = () => handleError();

    if (timeout > 0) {
      timeoutId = setTimeout(() => {
        if (!resolved) {
          console.warn(`[loadScript] Timeout loading: ${url}`);
          handleError(new Error(`Script load timeout: ${url}`));
        }
      }, timeout);
    }

    document.head.appendChild(script);
  });
}
